// Standard Solar Ground Mounted Configurations and Suggestions
// Complete Hierarchy: Project → Block → Inverter → String → Module

export interface StandardChildConfig {
  assetType: string;
  suggestedQuantity: number;
  unitsPerParent: number;
  namingPattern: string;
  description: string;
  isRecommended?: boolean;
}

export interface StandardParentConfig {
  assetType: string;
  description: string;
  isRecommended?: boolean;
}

// Standard hierarchy for solar ground mounted
export const SOLAR_HIERARCHY: Record<string, {
  recommendedChildren: StandardChildConfig[];
  recommendedParents: StandardParentConfig[];
  level: number;
  category: 'project' | 'site' | 'block' | 'conversion' | 'collection' | 'generation' | 'transmission' | 'protection' | 'bos' | 'monitoring' | 'security';
  description?: string;
}> = {
  // ============================================
  // TOP LEVEL - PROJECT & SITE
  // ============================================
  'Solar Plant Project': {
    level: 0,
    category: 'project',
    description: 'Top parent - entire solar plant',
    recommendedChildren: [
      {
        assetType: 'Plant',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'PLANT-{seq}',
        description: 'Main plant/site container',
        isRecommended: true
      },
      {
        assetType: 'Block',
        suggestedQuantity: 4,
        unitsPerParent: 4,
        namingPattern: 'BLK-{seq}',
        description: 'Direct blocks without plant level',
        isRecommended: false
      }
    ],
    recommendedParents: []
  },

  'Plant': {
    level: 1,
    category: 'site',
    description: 'Plant/Site level',
    recommendedChildren: [
      {
        assetType: 'Block',
        suggestedQuantity: 4,
        unitsPerParent: 4,
        namingPattern: 'BLK-{seq}',
        description: 'Blocks/Zones/Inverter Stations',
        isRecommended: true
      },
      {
        assetType: 'Inverter',
        suggestedQuantity: 16,
        unitsPerParent: 16,
        namingPattern: 'INV-{seq}',
        description: 'Direct inverters without blocks',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Solar Plant Project',
        description: 'Plant belongs to project',
        isRecommended: true
      }
    ]
  },

  'Block': {
    level: 2,
    category: 'block',
    description: 'Block/Zone/Inverter Station grouping',
    recommendedChildren: [
      {
        assetType: 'Inverter',
        suggestedQuantity: 4,
        unitsPerParent: 4,
        namingPattern: 'INV-{parent}-{seq}',
        description: 'Standard 4 inverters per block',
        isRecommended: true
      },
      {
        assetType: 'Inverter',
        suggestedQuantity: 8,
        unitsPerParent: 8,
        namingPattern: 'INV-{parent}-{seq}',
        description: 'Large block with 8 inverters',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Plant',
        description: 'Block belongs to plant',
        isRecommended: true
      },
      {
        assetType: 'Solar Plant Project',
        description: 'Direct block under project',
        isRecommended: false
      }
    ]
  },

  // ============================================
  // DC SIDE - INVERTER → STRING → MODULE
  // ============================================
  'Inverter': {
    level: 3,
    category: 'conversion',
    description: 'DC to AC conversion - Central equipment',
    recommendedChildren: [
      {
        assetType: 'String',
        suggestedQuantity: 10,
        unitsPerParent: 10,
        namingPattern: 'STR-{parent}-{seq}',
        description: 'Standard 10 strings per inverter',
        isRecommended: true
      },
      {
        assetType: 'String',
        suggestedQuantity: 12,
        unitsPerParent: 12,
        namingPattern: 'STR-{parent}-{seq}',
        description: '12 strings per inverter',
        isRecommended: false
      },
      {
        assetType: 'String',
        suggestedQuantity: 14,
        unitsPerParent: 14,
        namingPattern: 'STR-{parent}-{seq}',
        description: '14 strings per inverter',
        isRecommended: false
      },
      {
        assetType: 'ACDB',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'ACDB-{parent}',
        description: 'AC Distribution Board from inverter',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Block',
        description: 'Inverter belongs to block/zone',
        isRecommended: true
      },
      {
        assetType: 'Plant',
        description: 'Direct inverter under plant',
        isRecommended: false
      },
      {
        assetType: 'ACDB',
        description: 'Via AC distribution board',
        isRecommended: false
      }
    ]
  },

  'String': {
    level: 4,
    category: 'collection',
    description: 'DC String - connects modules in series',
    recommendedChildren: [
      {
        assetType: 'Solar Module',
        suggestedQuantity: 28,
        unitsPerParent: 28,
        namingPattern: 'MOD-{parent}-{seq}',
        description: 'Standard 28 modules per string (Most Common)',
        isRecommended: true
      },
      {
        assetType: 'Solar Module',
        suggestedQuantity: 26,
        unitsPerParent: 26,
        namingPattern: 'MOD-{parent}-{seq}',
        description: 'Alternative 26 modules per string',
        isRecommended: false
      },
      {
        assetType: 'Solar Module',
        suggestedQuantity: 24,
        unitsPerParent: 24,
        namingPattern: 'MOD-{parent}-{seq}',
        description: 'Alternative 24 modules per string',
        isRecommended: false
      },
      {
        assetType: 'Solar Module',
        suggestedQuantity: 30,
        unitsPerParent: 30,
        namingPattern: 'MOD-{parent}-{seq}',
        description: 'Alternative 30 modules per string',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Inverter',
        description: 'Strings connect to inverter DC input',
        isRecommended: true
      },
      {
        assetType: 'String Combiner Box',
        description: 'Via DC combiner box',
        isRecommended: false
      },
      {
        assetType: 'DC Junction Box',
        description: 'Via DC junction box',
        isRecommended: false
      }
    ]
  },

  'Solar Module': {
    level: 5,
    category: 'generation',
    description: 'PV Module - generates DC power',
    recommendedChildren: [
      {
        assetType: 'MC4 Connector',
        suggestedQuantity: 2,
        unitsPerParent: 2,
        namingPattern: 'MC4-{parent}-{seq}',
        description: 'DC connectors for module',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'String',
        description: 'Modules connect to DC strings',
        isRecommended: true
      }
    ]
  },

  // ============================================
  // DC OPTIONAL EQUIPMENT (Between String → Inverter)
  // ============================================
  'String Combiner Box': {
    level: 3,
    category: 'protection',
    description: 'SCB - combines multiple strings',
    recommendedChildren: [
      {
        assetType: 'String',
        suggestedQuantity: 24,
        unitsPerParent: 24,
        namingPattern: 'STR-{parent}-{seq}',
        description: 'Standard 24 strings per combiner box',
        isRecommended: true
      },
      {
        assetType: 'DC Isolator',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'DCISO-{parent}',
        description: 'DC isolation switch',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Inverter',
        description: 'SCB connects to inverter',
        isRecommended: true
      },
      {
        assetType: 'Block',
        description: 'SCB at block level',
        isRecommended: false
      }
    ]
  },

  'DC Junction Box': {
    level: 3,
    category: 'protection',
    description: 'DCJB - DC junction/connection box',
    recommendedChildren: [
      {
        assetType: 'String',
        suggestedQuantity: 16,
        unitsPerParent: 16,
        namingPattern: 'STR-{parent}-{seq}',
        description: 'Strings per DCJB',
        isRecommended: true
      }
    ],
    recommendedParents: [
      {
        assetType: 'Inverter',
        description: 'DCJB connects to inverter',
        isRecommended: true
      }
    ]
  },

  'DC Isolator': {
    level: 3,
    category: 'protection',
    description: 'DC Switch/Isolator for safety',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Inverter',
        description: 'DC isolator before inverter',
        isRecommended: true
      },
      {
        assetType: 'String Combiner Box',
        description: 'After SCB',
        isRecommended: false
      }
    ]
  },

  'SPD DC': {
    level: 3,
    category: 'protection',
    description: 'Surge Protection Device - DC side',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'String Combiner Box',
        description: 'SPD in SCB',
        isRecommended: true
      },
      {
        assetType: 'Inverter',
        description: 'SPD at inverter DC input',
        isRecommended: false
      }
    ]
  },

  // ============================================
  // AC SIDE - INVERTER → ACDB → TRANSFORMER → GRID
  // ============================================
  'ACDB': {
    level: 3,
    category: 'protection',
    description: 'AC Distribution Board',
    recommendedChildren: [
      {
        assetType: 'Breaker',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'BKR-{parent}',
        description: 'Main breaker/MCCB',
        isRecommended: true
      },
      {
        assetType: 'SPD AC',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'SPD-{parent}',
        description: 'AC surge protection',
        isRecommended: false
      },
      {
        assetType: 'Transformer',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'TRF-{parent}',
        description: 'Step-up transformer',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Inverter',
        description: 'ACDB from inverter AC output',
        isRecommended: true
      }
    ]
  },

  'Breaker': {
    level: 4,
    category: 'protection',
    description: 'Circuit breaker/MCCB/ACB',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'ACDB',
        description: 'Breaker in ACDB',
        isRecommended: true
      },
      {
        assetType: 'LT Panel',
        description: 'Breaker in LT panel',
        isRecommended: false
      },
      {
        assetType: 'HT Panel',
        description: 'Breaker in HT panel',
        isRecommended: false
      }
    ]
  },

  'SPD AC': {
    level: 4,
    category: 'protection',
    description: 'Surge Protection Device - AC side',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'ACDB',
        description: 'SPD in ACDB',
        isRecommended: true
      },
      {
        assetType: 'LT Panel',
        description: 'SPD in LT panel',
        isRecommended: false
      }
    ]
  },

  'Transformer': {
    level: 2,
    category: 'transmission',
    description: 'Step-up transformer (LV to HV)',
    recommendedChildren: [
      {
        assetType: 'Inverter',
        suggestedQuantity: 4,
        unitsPerParent: 4,
        namingPattern: 'INV-{parent}-{seq}',
        description: 'Standard 4 inverters per transformer',
        isRecommended: true
      },
      {
        assetType: 'LT Panel',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'LT-{parent}',
        description: 'Low Tension panel',
        isRecommended: false
      },
      {
        assetType: 'HT Panel',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'HT-{parent}',
        description: 'High Tension panel/RMU',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'ACDB',
        description: 'Transformer after ACDB',
        isRecommended: true
      },
      {
        assetType: 'Block',
        description: 'Block-level transformer',
        isRecommended: false
      },
      {
        assetType: 'Plant',
        description: 'Plant-level transformer',
        isRecommended: false
      }
    ]
  },

  'LT Panel': {
    level: 3,
    category: 'transmission',
    description: 'Low Tension Panel',
    recommendedChildren: [
      {
        assetType: 'Breaker',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'BKR-{parent}',
        description: 'LT breaker',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Transformer',
        description: 'LT side of transformer',
        isRecommended: true
      }
    ]
  },

  'HT Panel': {
    level: 3,
    category: 'transmission',
    description: 'High Tension Panel / RMU',
    recommendedChildren: [
      {
        assetType: 'Metering Panel',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'MTR-{parent}',
        description: 'ABT Meter panel',
        isRecommended: true
      },
      {
        assetType: 'Grid Connection',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'GRID-{parent}',
        description: 'Grid interconnection point',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Transformer',
        description: 'HT side of transformer',
        isRecommended: true
      }
    ]
  },

  'Metering Panel': {
    level: 4,
    category: 'transmission',
    description: 'Metering / ABT Meter',
    recommendedChildren: [
      {
        assetType: 'Grid Connection',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'GRID-{parent}',
        description: 'To grid point',
        isRecommended: true
      }
    ],
    recommendedParents: [
      {
        assetType: 'HT Panel',
        description: 'Metering at HT panel',
        isRecommended: true
      }
    ]
  },

  'Grid Connection': {
    level: 1,
    category: 'transmission',
    description: 'Grid interconnection point (final)',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Metering Panel',
        description: 'After metering',
        isRecommended: true
      },
      {
        assetType: 'HT Panel',
        description: 'Direct from HT panel',
        isRecommended: false
      },
      {
        assetType: 'Switchyard',
        description: 'Via switchyard',
        isRecommended: false
      }
    ]
  },

  'Switchyard': {
    level: 1,
    category: 'transmission',
    description: 'High voltage switchyard (optional for large plants)',
    recommendedChildren: [
      {
        assetType: 'Grid Connection',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'GRID',
        description: 'Switchyard to grid',
        isRecommended: true
      }
    ],
    recommendedParents: [
      {
        assetType: 'Plant',
        description: 'Plant-level switchyard',
        isRecommended: true
      }
    ]
  },

  // ============================================
  // CABLES
  // ============================================
  'DC Cable': {
    level: 4,
    category: 'collection',
    description: 'DC cable for string connections',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'String',
        description: 'DC cable for strings',
        isRecommended: true
      },
      {
        assetType: 'Solar Module',
        description: 'Inter-module cables',
        isRecommended: false
      }
    ]
  },

  'AC Cable': {
    level: 3,
    category: 'transmission',
    description: 'AC cable from inverter',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Inverter',
        description: 'AC cable from inverter',
        isRecommended: true
      },
      {
        assetType: 'ACDB',
        description: 'AC cable from ACDB',
        isRecommended: false
      }
    ]
  },

  // ============================================
  // BOS - MOUNTING STRUCTURES
  // ============================================
  'Structure System': {
    level: 2,
    category: 'bos',
    description: 'Mounting structure system',
    recommendedChildren: [
      {
        assetType: 'Mounting Table',
        suggestedQuantity: 100,
        unitsPerParent: 100,
        namingPattern: 'TBL-{seq}',
        description: 'Module mounting tables',
        isRecommended: true
      }
    ],
    recommendedParents: [
      {
        assetType: 'Block',
        description: 'Structures per block',
        isRecommended: true
      },
      {
        assetType: 'Plant',
        description: 'Plant-level structures',
        isRecommended: false
      }
    ]
  },

  'Mounting Table': {
    level: 3,
    category: 'bos',
    description: 'Table/Mount structure for modules',
    recommendedChildren: [
      {
        assetType: 'Foundation',
        suggestedQuantity: 4,
        unitsPerParent: 4,
        namingPattern: 'PILE-{parent}-{seq}',
        description: 'Piles/foundations per table',
        isRecommended: true
      },
      {
        assetType: 'Rails',
        suggestedQuantity: 6,
        unitsPerParent: 6,
        namingPattern: 'RAIL-{parent}-{seq}',
        description: 'Rails/purlins per table',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Structure System',
        description: 'Tables in structure system',
        isRecommended: true
      }
    ]
  },

  'Foundation': {
    level: 4,
    category: 'bos',
    description: 'Piles/Foundation for structures',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Mounting Table',
        description: 'Foundation for tables',
        isRecommended: true
      }
    ]
  },

  'Rails': {
    level: 4,
    category: 'bos',
    description: 'Rails/Purlins for module mounting',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Mounting Table',
        description: 'Rails on tables',
        isRecommended: true
      }
    ]
  },

  // ============================================
  // BOS - EARTHING & LIGHTNING
  // ============================================
  'Earthing System': {
    level: 2,
    category: 'bos',
    description: 'Earthing & Lightning Protection',
    recommendedChildren: [
      {
        assetType: 'Earth Pit',
        suggestedQuantity: 50,
        unitsPerParent: 50,
        namingPattern: 'EP-{seq}',
        description: 'Earth pits across site',
        isRecommended: true
      },
      {
        assetType: 'Lightning Arrestor',
        suggestedQuantity: 10,
        unitsPerParent: 10,
        namingPattern: 'LA-{seq}',
        description: 'Lightning arrestors',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Plant',
        description: 'Plant-level earthing',
        isRecommended: true
      }
    ]
  },

  'Earth Pit': {
    level: 3,
    category: 'bos',
    description: 'Earth pit for grounding',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Earthing System',
        description: 'Part of earthing system',
        isRecommended: true
      }
    ]
  },

  'Lightning Arrestor': {
    level: 3,
    category: 'bos',
    description: 'Lightning protection device',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Earthing System',
        description: 'Part of earthing system',
        isRecommended: true
      }
    ]
  },

  // ============================================
  // MONITORING - SCADA & WEATHER
  // ============================================
  'SCADA System': {
    level: 2,
    category: 'monitoring',
    description: 'SCADA/Monitoring System',
    recommendedChildren: [
      {
        assetType: 'SCADA Server',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'SCADA-SRV',
        description: 'SCADA server/gateway',
        isRecommended: true
      },
      {
        assetType: 'Data Logger',
        suggestedQuantity: 10,
        unitsPerParent: 10,
        namingPattern: 'DL-{seq}',
        description: 'Data loggers',
        isRecommended: true
      },
      {
        assetType: 'Weather Station',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'WS-{seq}',
        description: 'Weather monitoring station',
        isRecommended: true
      }
    ],
    recommendedParents: [
      {
        assetType: 'Plant',
        description: 'Plant-level SCADA',
        isRecommended: true
      }
    ]
  },

  'SCADA Server': {
    level: 3,
    category: 'monitoring',
    description: 'SCADA Server/Gateway',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'SCADA System',
        description: 'Part of SCADA system',
        isRecommended: true
      }
    ]
  },

  'Data Logger': {
    level: 3,
    category: 'monitoring',
    description: 'Data logger for monitoring',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'SCADA System',
        description: 'Part of SCADA system',
        isRecommended: true
      },
      {
        assetType: 'Inverter',
        description: 'Inverter monitoring node',
        isRecommended: false
      }
    ]
  },

  'Weather Station': {
    level: 3,
    category: 'monitoring',
    description: 'Weather monitoring station',
    recommendedChildren: [
      {
        assetType: 'Pyranometer',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'PYR-{parent}',
        description: 'Solar irradiance sensor',
        isRecommended: true
      },
      {
        assetType: 'Temperature Sensor',
        suggestedQuantity: 2,
        unitsPerParent: 2,
        namingPattern: 'TEMP-{parent}-{seq}',
        description: 'Module & ambient temp sensors',
        isRecommended: true
      },
      {
        assetType: 'Wind Sensor',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'WIND-{parent}',
        description: 'Wind speed sensor',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'SCADA System',
        description: 'Part of SCADA system',
        isRecommended: true
      }
    ]
  },

  'Pyranometer': {
    level: 4,
    category: 'monitoring',
    description: 'Solar irradiance sensor',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Weather Station',
        description: 'Part of weather station',
        isRecommended: true
      }
    ]
  },

  'Temperature Sensor': {
    level: 4,
    category: 'monitoring',
    description: 'Temperature sensor (module/ambient)',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Weather Station',
        description: 'Part of weather station',
        isRecommended: true
      }
    ]
  },

  'Wind Sensor': {
    level: 4,
    category: 'monitoring',
    description: 'Wind speed/direction sensor',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Weather Station',
        description: 'Part of weather station',
        isRecommended: true
      }
    ]
  },

  // ============================================
  // SECURITY SYSTEM
  // ============================================
  'Security System': {
    level: 2,
    category: 'security',
    description: 'Site security system',
    recommendedChildren: [
      {
        assetType: 'CCTV Camera',
        suggestedQuantity: 20,
        unitsPerParent: 20,
        namingPattern: 'CAM-{seq}',
        description: 'Security cameras',
        isRecommended: true
      },
      {
        assetType: 'NVR',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'NVR-{seq}',
        description: 'Network Video Recorder',
        isRecommended: true
      },
      {
        assetType: 'Fence',
        suggestedQuantity: 1,
        unitsPerParent: 1,
        namingPattern: 'FENCE',
        description: 'Perimeter fencing',
        isRecommended: false
      }
    ],
    recommendedParents: [
      {
        assetType: 'Plant',
        description: 'Plant-level security',
        isRecommended: true
      }
    ]
  },

  'CCTV Camera': {
    level: 3,
    category: 'security',
    description: 'Security camera',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Security System',
        description: 'Part of security system',
        isRecommended: true
      }
    ]
  },

  'NVR': {
    level: 3,
    category: 'security',
    description: 'Network Video Recorder',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Security System',
        description: 'Part of security system',
        isRecommended: true
      }
    ]
  },

  'Fence': {
    level: 3,
    category: 'security',
    description: 'Perimeter fencing',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Security System',
        description: 'Part of security system',
        isRecommended: true
      }
    ]
  },

  // ============================================
  // OPTIONAL/MISC COMPONENTS
  // ============================================
  'MC4 Connector': {
    level: 6,
    category: 'generation',
    description: 'DC connector for modules',
    recommendedChildren: [],
    recommendedParents: [
      {
        assetType: 'Solar Module',
        description: 'Module connectors',
        isRecommended: true
      }
    ]
  }
};

// Get recommended children for an asset type
export function getRecommendedChildren(assetType: string): StandardChildConfig[] {
  // Normalize asset type name (handle variations)
  const normalizedType = assetType.trim();
  
  // Try exact match first
  let config = SOLAR_HIERARCHY[normalizedType];
  
  // If no exact match, try case-insensitive match
  if (!config) {
    const key = Object.keys(SOLAR_HIERARCHY).find(
      k => k.toLowerCase() === normalizedType.toLowerCase()
    );
    if (key) {
      config = SOLAR_HIERARCHY[key];
    }
  }
  
  if (!config) {
    return [];
  }
  
  // CRITICAL: Filter out same asset type to prevent circular suggestions
  // e.g., String should NOT suggest String as child
  return config.recommendedChildren.filter(child => 
    child.assetType !== assetType && 
    child.assetType.toLowerCase() !== assetType.toLowerCase()
  );
}

// Get recommended parents for an asset type
export function getRecommendedParents(assetType: string): StandardParentConfig[] {
  const normalizedType = assetType.trim();
  let config = SOLAR_HIERARCHY[normalizedType];
  
  if (!config) {
    const key = Object.keys(SOLAR_HIERARCHY).find(
      k => k.toLowerCase() === normalizedType.toLowerCase()
    );
    if (key) {
      config = SOLAR_HIERARCHY[key];
    }
  }
  
  if (!config) {
    return [];
  }
  
  // CRITICAL: Filter out same asset type to prevent circular suggestions
  return config.recommendedParents.filter(parent => 
    parent.assetType !== assetType &&
    parent.assetType.toLowerCase() !== assetType.toLowerCase()
  );
}

// Check if one asset can be parent of another
export function canBeParent(parentAssetType: string, childAssetType: string): boolean {
  // CRITICAL: Same asset type cannot be parent of itself
  if (parentAssetType === childAssetType || 
      parentAssetType.toLowerCase() === childAssetType.toLowerCase()) {
    return false;
  }
  
  const childConfig = SOLAR_HIERARCHY[childAssetType];
  if (!childConfig) return true; // Unknown types allowed for flexibility
  
  const recommendedParents = childConfig.recommendedParents;
  if (recommendedParents.length === 0) return false; // Child is root level
  
  return recommendedParents.some(p => 
    p.assetType === parentAssetType ||
    p.assetType.toLowerCase() === parentAssetType.toLowerCase()
  );
}

// Get standard configuration for quick setup
export function getStandardChildConfig(parentAssetType: string, childAssetType: string): StandardChildConfig | null {
  // CRITICAL: Cannot get config if same asset type
  if (parentAssetType === childAssetType ||
      parentAssetType.toLowerCase() === childAssetType.toLowerCase()) {
    return null;
  }
  
  const parentConfig = SOLAR_HIERARCHY[parentAssetType];
  if (!parentConfig) return null;
  
  const childConfig = parentConfig.recommendedChildren.find(c => 
    c.assetType === childAssetType ||
    c.assetType.toLowerCase() === childAssetType.toLowerCase()
  );
  return childConfig || null;
}

// Get asset category for intelligent grouping
export function getAssetCategory(assetType: string): string {
  const config = SOLAR_HIERARCHY[assetType];
  return config ? config.category : 'unknown';
}

// Get asset level for hierarchy depth
export function getAssetLevel(assetType: string): number {
  const config = SOLAR_HIERARCHY[assetType];
  return config ? config.level : 999; // Unknown assets go to bottom
}

// Calculate total equipment for standard setup
export function calculateStandardEquipment(configuration: {
  modules: number;
  modulesPerString: number;
  stringsPerInverter: number;
  invertersPerBlock: number;
  blocks: number;
}): {
  modules: number;
  strings: number;
  inverters: number;
  blocks: number;
  total: number;
} {
  const { modules, modulesPerString, stringsPerInverter, invertersPerBlock, blocks } = configuration;
  
  const strings = Math.ceil(modules / modulesPerString);
  const inverters = Math.ceil(strings / stringsPerInverter);
  const blocksNeeded = Math.ceil(inverters / invertersPerBlock);
  
  return {
    modules,
    strings,
    inverters,
    blocks: blocksNeeded || blocks,
    total: modules + strings + inverters + blocksNeeded
  };
}

// Get full path recommendation from top to specific asset
export function getRecommendedPath(targetAssetType: string): string[] {
  const path: string[] = [];
  let currentType = targetAssetType;
  
  // Build path backwards from target to root
  let iterations = 0;
  const MAX_ITERATIONS = 20; // Prevent infinite loops
  
  while (currentType && iterations < MAX_ITERATIONS) {
    path.unshift(currentType);
    
    const parents = getRecommendedParents(currentType);
    if (parents.length === 0) break;
    
    // Get the recommended parent (or first parent if none marked recommended)
    const recommendedParent = parents.find(p => p.isRecommended) || parents[0];
    if (!recommendedParent) break;
    
    // Check for circular reference
    if (path.includes(recommendedParent.assetType)) break;
    
    currentType = recommendedParent.assetType;
    iterations++;
  }
  
  return path;
}

// Validate if a proposed hierarchy path is valid
export function isValidHierarchyPath(path: string[]): boolean {
  if (path.length < 2) return true; // Single item is always valid
  
  for (let i = 0; i < path.length - 1; i++) {
    const parent = path[i];
    const child = path[i + 1];
    
    // Check for same asset type
    if (parent === child) return false;
    
    // Check if child can have this parent
    if (!canBeParent(parent, child)) return false;
  }
  
  return true;
}
